import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { supabase } from '@/lib/supabase';

interface ArticleFromDB {
  slug: string;
  title: string;
  category: string;
  excerpt: string | null;
  image_url: string | null;
  created_at: string;
}

interface ArticleUI {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categoryName: string;
  image: string;
  date: string;
}

const catLabels: Record<string, string> = {
  'company-news': 'Company News',
  'industry-insight': 'Industry Insight',
  'partnerships': 'Partnerships',
  'trade-updates': 'Trade Updates',
  'case-studies': 'Case Studies',
};

export default function LatestNews() {
  const [articles, setArticles] = useState<ArticleUI[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatest = async () => {
      try {
        const { data } = await supabase
          .from('articles')
          .select('slug,title,category,excerpt,image_url,created_at')
          .order('created_at', { ascending: false })
          .limit(3);

        if (data) {
          setArticles(data.map((a: ArticleFromDB) => ({
            id: a.slug,
            slug: a.slug,
            title: a.title,
            excerpt: a.excerpt || '',
            category: a.category,
            categoryName: catLabels[a.category] || a.category,
            image: a.image_url || 'https://readdy.ai/api/search-image?query=Modern%20corporate%20business%20news%20abstract%20professional%20setting%20clean%20composition%20warm%20lighting&width=800&height=600&seq=latest-placeholder&orientation=landscape',
            date: a.created_at,
          })));
        }
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchLatest();
  }, []);

  if (loading || articles.length === 0) return null;

  return (
    <section className="relative py-20 md:py-28 bg-background-100">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-14 md:mb-20 gap-4"
        >
          <div>
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">
              Insights
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950">
              Latest News &amp; Insights
            </h2>
            <p className="text-foreground-600 max-w-xl mt-3 text-sm md:text-base leading-relaxed">
              Stay informed with the latest developments, market insights, and company updates.
            </p>
          </div>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            View All Articles
            <i className="ri-arrow-right-line"></i>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {articles.map((article, index) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400 cursor-pointer"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-accent-600 rounded-full">
                    {article.categoryName}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <time className="text-xs text-foreground-400 mb-3 block">
                  {new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                  {article.excerpt}
                </p>
                <span className="text-xs font-semibold text-accent-600 group-hover:text-accent-500 transition-colors duration-200 flex items-center gap-1 whitespace-nowrap">
                  Read More <i className="ri-arrow-right-s-line"></i>
                </span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}